import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronDown, Copy, MapPin, Calendar, CheckCircle2, ShieldCheck, Zap, Globe } from "lucide-react";

// 1. IMPORT ALL IMAGES HERE
import valentineImg from "../images/offer-page-bg.webp";
import truckVanImg from "../images/truckvan-bannner.webp";

// 2. CREATE AN IMAGE MAP
const IMAGE_MAP = {
  "valentine-special": valentineImg,
  "truck-van-offer": truckVanImg,

};

// 3. DATA CENTER
const OFFERS_CONTENT = {
  "valentine-special": {
    "title": "Valentine’s Day Special",
    "subTitle": "Fall in Love with the Open Road!",
    "description": "Celebrate the season of love with exclusive savings on car rentals worldwide. Whether it's a romantic getaway or a solo adventure, unlock amazing discounts with Aombre Car Rental.",
    "promoCode": "VAL10",
    "expiry": "Feb 14th, 2026",
    "terms": [
      "Enjoy exclusive Valentine's savings on car rentals worldwide.",
      "Use promo code VAL10 to unlock amazing discounts.",
      "Discount applies to total base rental charges.",
      "Offer valid only on prepaid 'Pay Now' reservations; not applicable to 'Pay Later' bookings.",
      "Offer valid at worldwide Car Rental locations.",
      "Booking valid until Feb 14th, 2026.",
      "Rentals must be checked out no later than Mar 12th, 2026.",
      "Offer cannot be combined with any other promotion, coupon, or discount.",
      "Minimum rental period: 2 days or more.",
      "Offer subject to vehicle availability and may not apply to select online rates at aombre.com .",
      "Car rental return restrictions may apply.",
      "Holiday and long weekend blackout dates may apply.",
      "Offers are subject to change or withdrawal without prior notice.",
      "Renter must meet Aombre’ age, driver, and credit card requirements.",
      "Additional restrictions may apply – please review full rental policy at checkout."
    ]
  },
  "truck-van-offer": {
    "title": "Move with Love",
    "subTitle": "Spacious Solutions for Every Journey",
    "description": "Making a big move or need extra space? Get the vehicle you need for less. Our trucks and vans are perfect for transporting your world with ease at select Ontario locations.",
    "promoCode": "MOVE15",
    "expiry": "Feb 15th, 2026",
    "terms": [
      "Offer is available for Toronto and Ottawa locations only.",
      "Use the promo code MOVE15 to claim this offer.",
      "Offer may not be used in conjunction with any other promo code, promotion, or offer.",
      "Bookings must be made by February 15, 2026.",
      "Discount valid on rentals checked out no later than Mar 12th, 2026.",
      "Offer valid only on prepaid (“Pay Now”) reservations and not applicable to “Pay Later” bookings",
      "Minimum rental period: 2 days or more.",
      "Offer is subject to vehicle availability and may not be available on select online rates at aombre.com .",
      "Car rental return restrictions may apply.",
      "Offers are subject to change without notice.",
      "Long weekend/holidays and other blackout periods may apply.",
      "Renter must meet Aombre age, driver, and credit card requirements.",
      "Renter must meet our guidelines policy.",
      "Additional restrictions may apply, refer to rental policy page for more information. You will find the rental policy page at the checkout."
    ]
  }
};

function OfferDetails() {
  const { slug } = useParams(); 
  const navigate = useNavigate();
  
  const [showTC, setShowTC] = useState(false);
  const [copied, setCopied] = useState(false);

  // Get data and image based on the URL slug
  const currentOffer = OFFERS_CONTENT[slug];
  const currentImage = IMAGE_MAP[slug] || valentineImg; // Fallback to valentineImg if not found

  useEffect(() => {
    if (!currentOffer) {
      navigate("/offers");
    }
    window.scrollTo(0, 0);
  }, [currentOffer, navigate]);

  if (!currentOffer) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(currentOffer.promoCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="w-full min-h-screen bg-[#f4f7f5] font-sans antialiased text-[#353535] mt-8 lg:mt-16">
      {/* Dynamic Hero Section */}
      <div className="relative h-[420px] w-full flex items-center justify-center overflow-hidden ">
        <img
          src={currentImage} // Uses the image from our IMAGE_MAP
          alt={currentOffer.title}
          className="absolute inset-0 w-full h-full object-cover scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#006039]/60 via-[#006039]/40 to-[#f4f7f5]" />
        
        <div className="relative z-10 text-center text-white px-4 mb-20">
           <h1 className="text-4xl md:text-6xl font-black tracking-tighter uppercase drop-shadow-2xl">
             {currentOffer.title}
           </h1>
           <p className="text-lg md:text-xl font-medium opacity-90 mt-2 tracking-widest uppercase">Premium Road Experiences</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 -mt-44 relative z-20 pb-24">
        <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-[0_32px_64px_-15px_rgba(0,96,57,0.15)] border border-white overflow-hidden">
          
          <div className="flex flex-col lg:flex-row">
            {/* Left Column */}
            <div className="lg:w-7/12 p-8 md:p-14 border-b lg:border-b-0 lg:border-r border-gray-100">
              <h2 className="text-3xl md:text-4xl font-bold text-[#ca8032] mb-6 leading-tight">
                {currentOffer.subTitle}
              </h2>
              <p className="text-[#353535] text-lg leading-relaxed mb-10 opacity-80">
                {currentOffer.description}
              </p>

              <div className="grid grid-cols-2 gap-6 mb-4">
                <div className="flex items-center gap-3 text-sm font-bold text-[#006039]"><ShieldCheck size={20} className="text-[#ca8032]" /> Fully Insured</div>
                <div className="flex items-center gap-3 text-sm font-bold text-[#006039]"><Zap size={20} className="text-[#ca8032]" /> Instant Booking</div>
                <div className="flex items-center gap-3 text-sm font-bold text-[#006039]"><Globe size={20} className="text-[#ca8032]" /> 100+ Locations</div>
                <div className="flex items-center gap-3 text-sm font-bold text-[#006039]"><MapPin size={20} className="text-[#ca8032]" /> Worldwide Access</div>
              </div>
            </div>

            {/* Right Column */}
            <div className="lg:w-5/12 bg-gray-50/50 p-8 md:p-14 flex flex-col justify-center space-y-8">
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 relative group">
                <span className="absolute -top-3 left-6 bg-[#006039] text-white text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-widest">
                  Promotion Code
                </span>
                <div onClick={handleCopy} className="flex items-center justify-between mt-2 cursor-pointer">
                  <span className="text-3xl font-mono font-black text-[#006039]">{currentOffer.promoCode}</span>
                  <div className="p-2 rounded-lg bg-gray-50 group-hover:bg-[#006039]/10 transition-colors">
                    {copied ? <CheckCircle2 size={24} className="text-green-600" /> : <Copy size={24} className="text-gray-400" />}
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 relative">
                <span className="absolute -top-3 left-6 bg-[#ca8032] text-white text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-widest">
                  Offer Expires
                </span>
                <div className="flex items-center gap-4 mt-2">
                  <Calendar size={28} className="text-[#ca8032]" />
                  <span className="text-2xl font-black text-[#353535]">{currentOffer.expiry}</span>
                </div>
              </div>

              <button 
                onClick={() => navigate("/")} 
                className="w-full bg-[#006039] hover:bg-[#004d2e] text-white font-black py-5 rounded-2xl transition-all shadow-xl hover:shadow-[#006039]/30 uppercase tracking-widest text-lg"
              >
                Book Your Trip Now
              </button>
            </div>
          </div>

          {/* T&C Section */}
          <div className="px-8 py-6 bg-white border-t border-gray-100 text-center">
            <button 
              onClick={() => setShowTC(!showTC)}
              className="inline-flex items-center gap-2 text-[#ca8032] hover:text-[#006039] transition-colors text-xs font-bold uppercase tracking-widest"
            >
              Terms and Conditions
              <ChevronDown size={16} className={`transition-transform duration-300 ${showTC ? 'rotate-180' : ''}`} />
            </button>

            <div className={`overflow-hidden transition-all duration-500 ease-in-out ${showTC ? 'max-h-[800px] opacity-100 mt-8' : 'max-h-0 opacity-0'}`}>
              <div className="bg-gray-50 p-8 rounded-3xl border border-gray-100 text-left">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {currentOffer.terms.map((item, index) => (
                    <div key={index} className="flex items-start gap-3 text-[13px] text-[#353535]/70 font-medium">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#ca8032] mt-1.5 shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OfferDetails;